import { Spinner } from "../../../components/ui/spinner";
import MCPListItem from "./MCPListItem";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  connections: MCPConnection[];
  selectedId: string | null;
  canManage: (id: string) => boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  updatingActiveId?: string | null;
  isLoading?: boolean;
  emptyMessage?: string;
  errorMessage?: string | null;
};

const MCPList = ({
  connections,
  selectedId,
  canManage,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
  updatingActiveId = null,
  isLoading = false,
  emptyMessage,
  errorMessage = null,
}: Props) => {
  const { t } = useTranslation();
  let body;

  if (isLoading && connections.length === 0) {
    body = (
      <div className="flex items-center justify-center px-6 py-12">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    );
  } else if (errorMessage) {
    body = (
      <div className="px-6 py-12 text-center text-sm text-destructive">
        {errorMessage}
      </div>
    );
  } else if (connections.length === 0) {
    body = (
      <div className="px-6 py-12 text-center text-sm text-muted-foreground">
        {emptyMessage ?? t("mcpPage.emptyConnections")}
      </div>
    );
  } else {
    body = (
      <ul>
        {connections.map((connection) => (
          <MCPListItem
            key={connection.id}
            connection={connection}
            isSelected={selectedId === connection.id}
            canManage={canManage(connection.id)}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleActive={onToggleActive}
            isUpdatingActive={updatingActiveId === connection.id}
          />
        ))}
      </ul>
    );
  }

  // No card around the list and no rules between rows: the design gives each row
  // its own outline, and only when it is hovered or selected.
  return <div>{body}</div>;
};

export default MCPList;
